import React, { useEffect, useState } from 'react';
import { Typography, makeStyles, Button, CircularProgress } from '@rbx/ui';
import { useTranslation, withTranslation } from '@rbx/intl';
import { CreatorHomeReengagementClient } from '@modules/clients/creatorHome';
import { TranslationNamespace } from '@modules/miscellaneous/localization';
import unifiedLoggerClient from '@modules/eventStream/unifiedLoggerClient';
import { useAuthentication } from '@modules/authentication/providers';
import {
  StarterPlaceParameterResults,
  StarterPlaceParameters,
} from '@modules/clients/ixpExperiments';
import { urls } from '@modules/miscellaneous/common';
import {
  createYourPlace,
  isValidTemplateId,
  getRobloxLinkForRootPlaceId,
  UniverseDescriptionTranslationKey,
} from '../../utils/createYourPlaceUtils';
import { yourPlaceImage } from '../../constants/assetConstants';
import useUploadAssetForPlaceMutation from '../../../miscellaneous/hooks/useUploadAssetForPlaceMutation';
import useUploadAutoGeneratedIconMutation from '../../../miscellaneous/hooks/useUploadAutoGeneratedPlaceIconMutation';

const useStyles = makeStyles()((theme) => ({
  card: {
    width: '100%',
    backgroundColor: theme.palette.surface[200],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingTop: '42px', // Special design for the create place card
    gap: '16px',
  },
  title: {
    textAlign: 'left',
  },
  description: {
    textAlign: 'left',
  },
  button: {
    width: '100%',
  },
  loadingContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  imageContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    flexGrow: 1,
  },
  image: {
    maxWidth: '100%',
    borderRadius: '4px', // Special design for the create place card
  },
  link: {
    color: theme.palette.content.standard,
  },
}));

export interface CreatePlaceCardProps {
  onConvert: (conversionName: string) => void;
  starterPlaceTemplateId: StarterPlaceParameterResults[StarterPlaceParameters.StarterPlaceTemplateId];
}

const CreatePlaceCard = ({ onConvert, starterPlaceTemplateId }: CreatePlaceCardProps) => {
  const { classes } = useStyles();
  const { translate } = useTranslation();
  const [isCreatePlaceLoading, setIsCreatePlaceLoading] = useState(false);
  const [isCheckingReengagement, setIsCheckingReengagement] = useState(true);
  const [doesYourPlaceExist, setDoesYourPlaceExist] = useState(false);
  const [rootPlaceId, setRootPlaceId] = useState<number | null>(null);

  useEffect(() => {
    const checkReengagementEntry = async () => {
      try {
        const response = await CreatorHomeReengagementClient.getYourPlaceReengagementEntry();

        if (response.hasValidEntry && response.rootPlaceId && response.universeId) {
          setDoesYourPlaceExist(true);
          setRootPlaceId(response.rootPlaceId);
          unifiedLoggerClient.logImpressionEvent({
            eventName: 'createPlaceCardPageLoadWithReengagement',
            parameters: {
              root_place_id: response.rootPlaceId?.toString() || '',
              universe_id: response.universeId?.toString() || '',
            },
          });
        } else {
          setDoesYourPlaceExist(false);
        }
      } catch {
        setDoesYourPlaceExist(false);
      } finally {
        setIsCheckingReengagement(false);
      }
    };

    checkReengagementEntry();
  }, []);
  const { user } = useAuthentication();
  const userId = user?.id;
  const { uploadAssetForPlaceAsync } = useUploadAssetForPlaceMutation();
  const { uploadAutoGeneratedPlaceIconAsync } = useUploadAutoGeneratedIconMutation();

  useEffect(() => {
    // Only log page load when the component is actually rendered with valid data
    if (isValidTemplateId(starterPlaceTemplateId)) {
      unifiedLoggerClient.logImpressionEvent({
        eventName: 'createPlaceCardPageLoad',
        parameters: {
          template_place_id: starterPlaceTemplateId.toString(),
          component: 'CreatePlaceCard',
          tag: 'createPlaceCard',
          section: 'onboarding',
        },
      });
    }
  }, [starterPlaceTemplateId]);

  const onClickContinueBuildingButton = () => {
    if (isCreatePlaceLoading || !rootPlaceId) {
      return;
    }

    setIsCreatePlaceLoading(true);
    onConvert('yourPlaceReengagementBannerContinueBuildingClicked');
    window.location.href = getRobloxLinkForRootPlaceId(rootPlaceId);
  };

  const onClickCreatePlaceButton = () => {
    if (isCreatePlaceLoading || !isValidTemplateId(starterPlaceTemplateId)) {
      return;
    }

    setIsCreatePlaceLoading(true);
    onConvert('clickCreatePlaceButton');
    createYourPlace({
      starterPlaceTemplateId,
      userId,
      uploadAssetForPlaceAsync,
      uploadAutoGeneratedPlaceIconAsync,
      placeDescription: translate(UniverseDescriptionTranslationKey, {
        studioSetupLink: `${urls.creatorHub.docs.getSettingUpStudioUrl()}`,
      }),
      onFailure: () => {
        // TODO lhoward 2026-01-13: Properly handle failure (user message, etc)
        setIsCreatePlaceLoading(false);
      },
    });
  };

  return (
    <div className={classes.card}>
      <div className={classes.imageContainer}>
        <img className={classes.image} src={yourPlaceImage} alt='Create Place Illustration' />
      </div>
      {isCheckingReengagement ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : (
        <React.Fragment>
          <Typography color='primary' variant='h6' className={classes.title}>
            {doesYourPlaceExist
              ? translate('Heading.YourPlaceReengagement')
              : translate('Heading.CreateYourFirstGame')}
          </Typography>
          <Typography color='primary' variant='body2' className={classes.description}>
            {doesYourPlaceExist
              ? translate('Description.YourPlaceReengagement')
              : translate('Description.CreateYourFirstGame')}
          </Typography>
          <Button
            className={classes.button}
            color='primaryBrand'
            variant='contained'
            size='large'
            onClick={() => {
              unifiedLoggerClient.logClickEvent({
                eventName: 'createPlaceCardRobloxStudio',
                parameters: {
                  doesYourPlaceExist: doesYourPlaceExist.toString(),
                },
              });
              setTimeout(() => {
                window.open(urls.creatorHub.docs.getSettingUpStudioUrl(), '_self');
              }, 100);
            }}>
            {translate('Action.GetRobloxStudio')}
          </Button>
          <Button
            className={classes.button}
            color='secondary'
            variant='contained'
            size='large'
            loading={isCheckingReengagement || isCreatePlaceLoading}
            onClick={doesYourPlaceExist ? onClickContinueBuildingButton : onClickCreatePlaceButton}>
            {doesYourPlaceExist
              ? translate('Action.YourPlaceReengagement')
              : translate('Action.StartCreating')}
          </Button>
        </React.Fragment>
      )}
    </div>
  );
};

export default withTranslation(CreatePlaceCard, [
  TranslationNamespace.Home,
  TranslationNamespace.Landing,
]);
